/**
 * Conversion between stored signature geometry (PDF points, bottom-left origin)
 * and the on-screen box the originator drags (CSS px, top-left origin).
 *
 * Both directions go through pdf.js's own `PageViewport` rather than a hand-
 * rolled `y = pageHeight - y` flip. The viewport already encodes the page's
 * scale, its `/MediaBox` offset (not every page starts at 0,0) and its `/Rotate`
 * — reimplementing that here would be three separate ways to be subtly wrong on
 * exactly the government forms this tool exists for.
 */
import type { PageViewport } from 'pdfjs-dist';

import type { SignaturePlacement } from '@/api/workflows';

/** An on-screen box, in CSS px relative to the rendered page's top-left. */
export type CssBox = {
  left: number;
  top: number;
  width: number;
  height: number;
};

/**
 * The authored signature box — the rectangle the signature actually fills.
 *
 * The anchor is stamped at the box's BOTTOM-LEFT corner and OneSpan grows the
 * widget up and to the right from there, so these dimensions describe the real
 * footprint rather than a hint. They never reach BMO
 * (`docs/save-application_v7.yaml` has no size member) — OneSpan's own growth
 * direction reproduces them from where the anchor sits, which is why this
 * constant is the single place the footprint is decided.
 *
 * Fixed rather than per-placement resizable: one consistent signature size
 * across a form is what an originator wants, and a resize handle would invite
 * per-box drift for no gain.
 */
export const SIGNATURE_BOX = { width: 150, height: 40 } as const;

/**
 * The same footprint for an initials placement — narrower, because a set of
 * initials is a fraction of a signature's width and the PDF areas they go in are
 * sized accordingly.
 *
 * Purely how the box reads on screen and where the anchor lands: the dimensions
 * never reach BMO or OneSpan (see `SIGNATURE_BOX`), which grows its own widget
 * from the stamped anchor. Toggling between the two rewrites `width` and leaves
 * `x`/`y` alone, so the box stays pinned to the anchor's bottom-left corner
 * rather than jumping.
 */
export const INITIALS_BOX = { width: 40, height: 40 } as const;

/**
 * The footprint of a companion text box the signer types into.
 *
 * **250pt wide, at a signature's height.** What these boxes are actually used
 * for is the address line on a sub-agreement, and a signature's 150pt holds a
 * signature — a street address overflows it, so the originator was placing a
 * box narrower than the text it collects. 250pt clears an address line while
 * staying inside the printed area it has to sit in; it is well inside BMO's own
 * precedent, since their reference package sizes the textarea at 705×65 in
 * `extractAnchor`. The height stays at a signature's, which keeps a page of
 * mixed boxes on one baseline grid; the square corners remain what tell an
 * input and a signature apart.
 *
 * **Indicative until BMO confirms they will take our dimensions.** The
 * capability exists on their side (see the textarea above); what is not yet
 * settled is whether their save-application pipeline will carry OUR numbers
 * through. Until it does, this decides where the anchor is stamped and how the
 * box reads on screen, and OneSpan's own default decides the widget's real
 * extent. See `tasks/esignature-input-placements-2026-09-23.md` (B-IN2).
 */
export const INPUT_BOX = { width: 250, height: SIGNATURE_BOX.height } as const;

/** The authored footprint for a signature placement. */
export function boxSizeFor(isInitials: boolean): { width: number; height: number } {
  return isInitials ? INITIALS_BOX : SIGNATURE_BOX;
}

/**
 * How far past the authored footprint the box is OUTLINED on screen, per side,
 * in CSS px.
 *
 * Presentation only. The rectangle every placement is stored, stamped, clamped
 * and validated against is the exact one above — this buffer is drawn around it
 * so the originator can see that a signature is about to sit flush against
 * neighbouring text or a page edge before OneSpan grows a widget into that
 * space. Nothing downstream reads it: the anchor still lands on the authored
 * bottom-left corner, and the saved `x`/`y`/`width`/`height` are untouched.
 *
 * CSS px rather than PDF points, so the margin stays the same visual thickness
 * at every zoom level — it is a reading aid, not a second footprint that should
 * scale with the page.
 */
export const PLACEMENT_PREVIEW_BUFFER_PX = 5;

/**
 * pdf.js types its coordinate helpers as bare `number[]`, so destructuring them
 * under `noUncheckedIndexedAccess` yields `number | undefined`. Reading through
 * this keeps the arithmetic below honest without scattering non-null assertions
 * over every conversion.
 */
function at(values: readonly number[], index: number): number {
  return values[index] ?? 0;
}

/** `[xMin, yMin, xMax, yMax]` of the page in PDF points, from the viewport's viewBox. */
function pageRect(viewport: PageViewport): [number, number, number, number] {
  const x0 = at(viewport.viewBox, 0);
  const y0 = at(viewport.viewBox, 1);
  const x1 = at(viewport.viewBox, 2);
  const y1 = at(viewport.viewBox, 3);
  return [Math.min(x0, x1), Math.min(y0, y1), Math.max(x0, x1), Math.max(y0, y1)];
}

/**
 * Stored placement → the absolutely-positioned overlay box.
 *
 * `convertToViewportRectangle` may return the corners in either order (it maps
 * through the page's rotation matrix), so the result is normalised the same way
 * `PdfAcroViewer` normalises AcroForm widget rects.
 */
export function placementToCssBox(placement: SignaturePlacement, viewport: PageViewport): CssBox {
  const rect = viewport.convertToViewportRectangle([
    placement.x,
    placement.y,
    placement.x + placement.width,
    placement.y + placement.height,
  ]) as number[];
  const x1 = at(rect, 0);
  const y1 = at(rect, 1);
  const x2 = at(rect, 2);
  const y2 = at(rect, 3);
  return {
    left: Math.min(x1, x2),
    top: Math.min(y1, y2),
    width: Math.abs(x2 - x1),
    height: Math.abs(y2 - y1),
  };
}

/**
 * The exact inverse: an on-screen box → its PDF-space origin.
 *
 * Converting BOTH corners and taking the minimum of each axis is what makes this
 * survive rotation — on a 90°-rotated page the CSS top-left corner is not the
 * PDF bottom-left corner, so converting one point and assuming would silently
 * offset every box on that page by its own height.
 */
export function cssBoxToPdfOrigin(box: CssBox, viewport: PageViewport): { x: number; y: number } {
  const topLeft = viewport.convertToPdfPoint(box.left, box.top) as number[];
  const bottomRight = viewport.convertToPdfPoint(
    box.left + box.width,
    box.top + box.height,
  ) as number[];
  return {
    x: Math.min(at(topLeft, 0), at(bottomRight, 0)),
    y: Math.min(at(topLeft, 1), at(bottomRight, 1)),
  };
}

/**
 * Pull a box back inside the page.
 *
 * A drag that ends past the edge would otherwise persist coordinates that put
 * the anchor text outside the `/MediaBox`, where pdf-lib writes it happily and
 * no viewer — and no OneSpan text extraction — ever sees it. Failing silently is
 * exactly the class of bug drag-and-drop placement exists to remove, so the box
 * is clamped rather than rejected.
 */
export function clampToPage(
  origin: { x: number; y: number },
  size: { width: number; height: number },
  viewport: PageViewport,
): { x: number; y: number } {
  const [xMin, yMin, xMax, yMax] = pageRect(viewport);
  // A page smaller than the box (never seen in practice, but a malformed
  // /MediaBox is not ours to trust) would give an inverted range; pinning to the
  // page origin beats returning a NaN-adjacent negative.
  const maxX = Math.max(xMin, xMax - size.width);
  const maxY = Math.max(yMin, yMax - size.height);
  return {
    x: Math.min(Math.max(origin.x, xMin), maxX),
    y: Math.min(Math.max(origin.y, yMin), maxY),
  };
}

/**
 * Where a freshly-dragged box lands: the overlay box moved by dnd-kit's pixel
 * delta, converted back to PDF points and clamped.
 *
 * Width and height come from the stored placement rather than the CSS box, so a
 * rounding error in the overlay can never shrink the authored 150×40 over
 * repeated drags.
 */
export function movePlacement<T extends SignaturePlacement>(
  placement: T,
  delta: { x: number; y: number },
  viewport: PageViewport,
): T {
  const box = placementToCssBox(placement, viewport);
  const moved = { ...box, left: box.left + delta.x, top: box.top + delta.y };
  const origin = cssBoxToPdfOrigin(moved, viewport);
  const clamped = clampToPage(origin, placement, viewport);
  return { ...placement, x: clamped.x, y: clamped.y };
}

/**
 * Where a click lands a NEW box: the pointer becomes the box's centre, so the
 * box appears under the cursor rather than hanging down-right of it.
 */
export function placementAtCssPoint(
  point: { x: number; y: number },
  size: { width: number; height: number },
  page: number,
  viewport: PageViewport,
): SignaturePlacement {
  const cssWidth = size.width * viewport.scale;
  const cssHeight = size.height * viewport.scale;
  const box: CssBox = {
    left: point.x - cssWidth / 2,
    top: point.y - cssHeight / 2,
    width: cssWidth,
    height: cssHeight,
  };
  const clamped = clampToPage(cssBoxToPdfOrigin(box, viewport), size, viewport);
  return { page, x: clamped.x, y: clamped.y, width: size.width, height: size.height };
}

/**
 * A date box's default position: directly below its signature, so the pair reads
 * as a unit before the originator drags it anywhere. Clamped, because a
 * signature sitting at the very bottom of a page has no room beneath it.
 *
 * `size` is the measured extent of the format tokens at the chosen point size
 * (`dateFormatMetrics.ts`), not a constant — a date box IS its placeholder text,
 * so its footprint comes from the text rather than from a rectangle nobody
 * chose.
 */
export function defaultDatePlacement(
  signature: SignaturePlacement,
  size: { width: number; height: number },
): SignaturePlacement {
  return {
    page: signature.page,
    x: signature.x,
    // PDF y grows upward, so "below" is a subtraction. Floored at 0 rather than
    // clamped through the viewport: this runs at authoring time where the
    // viewport for that page may not be mounted, and the placement page clamps
    // again on the first drag.
    y: Math.max(0, signature.y - size.height),
    width: size.width,
    height: size.height,
  };
}

/**
 * How far a duplicated companion box — a date or an input — is offset from the
 * one it was copied from, in PDF points. Down and to the right, so repeated
 * duplicates cascade the way a duplicated window does — a copy dropped exactly on
 * top of its source looks like the button did nothing, and the originator drags
 * the wrong one.
 */
const BOX_DUPLICATE_OFFSET = { x: 8, y: 4 } as const;

/**
 * A second date box for a signature that already has one: the same size, offset
 * from its source so both are visible and separately draggable.
 *
 * Offset from the SOURCE rather than from the signature, so duplicating twice
 * gives three boxes rather than two in the same place. Same floor-at-0
 * convention as `defaultDatePlacement`, and for the same reason — the viewport
 * for the page may not be mounted here, and the placement page clamps properly
 * on the first drag.
 */
export function duplicateDatePlacement(source: SignaturePlacement): SignaturePlacement {
  return {
    ...source,
    x: source.x + BOX_DUPLICATE_OFFSET.x,
    y: Math.max(0, source.y - (source.height + BOX_DUPLICATE_OFFSET.y)),
  };
}

/**
 * An input box's default position: below its signature, and below anything
 * already added there.
 *
 * `existing` is how many input boxes the signature already carries in this
 * language, which steps each new one further down. Without it the second input
 * would land exactly on the first — and unlike a duplicate there is no source box
 * to cascade from, since this is the "add" path. A date box, if any, sits in the
 * first slot below the signature, so the offset starts one box lower; the
 * originator drags from there.
 *
 * Same floor-at-0 convention as `defaultDatePlacement`, and for the same reason:
 * the viewport for the page may not be mounted at authoring time, and the
 * placement page clamps properly on the first drag.
 */
export function defaultInputPlacement(
  signature: SignaturePlacement,
  existing: number,
): SignaturePlacement {
  return {
    page: signature.page,
    x: signature.x,
    y: Math.max(0, signature.y - INPUT_BOX.height * (existing + 2)),
    width: INPUT_BOX.width,
    height: INPUT_BOX.height,
  };
}

/**
 * Another input box for a signature that already has one, copied from the box the
 * originator clicked — the same relationship `duplicateDatePlacement` has to its
 * source, and offset the same way so repeated copies cascade instead of stacking.
 */
export function duplicateInputPlacement(source: SignaturePlacement): SignaturePlacement {
  return {
    ...source,
    x: source.x + BOX_DUPLICATE_OFFSET.x,
    y: Math.max(0, source.y - (source.height + BOX_DUPLICATE_OFFSET.y)),
  };
}
